import styled from '@emotion/styled';
import { Typography, tokens } from '@sunrun/experience-ui-components';
// eslint-disable-next-line import/no-restricted-paths
import { StatusLegend } from './StatusLegend';
import { PerformanceProfileData } from 'types/performance';
import { InfoIconPopover } from 'components/atoms/InfoIconPopover';
import { getIconForRankValue, RANK_MAP } from 'components/atoms/RankIcons';
import { formatDisplayNumber } from 'helpers/formatHelpers';
import { getMaxRankLevel, getNextRankMap } from 'helpers/runXThresholds';
import { NewDashboardOrgType } from 'helpers/repTypeHelper';
import { useUserProfile } from 'providers/UserProfile';

const getRunXRankValue = (
  runXStatus: PerformanceProfileData['RunXStatus'] | undefined,
): number => {
  if (runXStatus?.CurrentRankValue != null) {
    return runXStatus.CurrentRankValue;
  }

  const rankName = runXStatus?.CurrentRank;
  if (rankName != null && rankName in RANK_MAP) {
    return RANK_MAP[rankName as keyof typeof RANK_MAP];
  }

  return 0;
};

const RunXStatus = ({
  profileData,
  orgType,
}: {
  profileData: PerformanceProfileData | null;
  orgType: NewDashboardOrgType;
}) => {
  const { userProfile } = useUserProfile();
  const sellsSunrun = userProfile?.sellsSunrun === 'Yes';
  const maxRank = getMaxRankLevel(orgType, sellsSunrun);
  const currentRankValue = getRunXRankValue(profileData?.RunXStatus);
  const nextRankValue: number = Math.min(currentRankValue + 1, maxRank);
  const nextRankMap = getNextRankMap(orgType, sellsSunrun);
  const currentSraPoints = profileData?.RunXStatus?.CurrentSraPoints ?? 0;
  const currentIcPoints = profileData?.RunXStatus?.CurrentIcPoints ?? 0;

  return (
    <Section>
      <HeaderRow>
        <Typography>RunX status this quarter</Typography>
        <InfoIconPopover
          content={<StatusLegend orgType={orgType} sellsSunrun={sellsSunrun} />}
          contentAriaLabel={'RunX status levels'}
          triggerAriaLabel={'How RunX status levels work'}
        />
      </HeaderRow>
      <Columns>
        <StatusColumn>
          <ColumnBody>
            <IconSlot>
              {getIconForRankValue({
                rank: currentRankValue,
                sizePx: 72,
                opacity: 1,
                orgType,
              })}
            </IconSlot>
            <MetricsBlock>
              <Typography size="small" fontWeight="medium">
                Right now
              </Typography>
              <MetricRow>
                {orgType === 'STREET' && (
                  <div>
                    <TypographyWithLineHeight fontWeight="medium">
                      {formatDisplayNumber(currentSraPoints)}
                    </TypographyWithLineHeight>
                    <MetricLabel size="small">Sales</MetricLabel>
                  </div>
                )}
                <div>
                  <TypographyWithLineHeight fontWeight="medium">
                    {formatDisplayNumber(currentIcPoints)}
                  </TypographyWithLineHeight>
                  <MetricLabel size="small">Installs</MetricLabel>
                </div>
              </MetricRow>
            </MetricsBlock>
          </ColumnBody>
        </StatusColumn>
        <StatusColumn>
          <ColumnBody>
            <NextIconSlot>
              {getIconForRankValue({
                rank: nextRankValue,
                sizePx: 72,
                opacity: currentRankValue === maxRank ? 1 : 0.5,
                orgType,
              })}
            </NextIconSlot>
            <MetricsBlock>
              <Typography size="small" fontWeight="medium">
                What&apos;s next
              </Typography>
              <MetricRow>
                {nextRankValue != null && nextRankMap[nextRankValue]?.Sales && (
                  <div>
                    <TypographyWithLineHeight fontWeight="medium">
                      {formatDisplayNumber(
                        nextRankMap[nextRankValue]?.Sales ?? 0,
                      )}
                    </TypographyWithLineHeight>
                    <MetricLabel size="small">Sales</MetricLabel>
                  </div>
                )}
                {nextRankValue != null &&
                  nextRankMap[nextRankValue]?.Installs && (
                    <div>
                      <TypographyWithLineHeight fontWeight="medium">
                        {formatDisplayNumber(
                          nextRankMap[nextRankValue]?.Installs ?? 0,
                        )}
                      </TypographyWithLineHeight>
                      <MetricLabel size="small">Installs</MetricLabel>
                    </div>
                  )}
              </MetricRow>
            </MetricsBlock>
          </ColumnBody>
        </StatusColumn>
      </Columns>
    </Section>
  );
};

const TypographyWithLineHeight = styled(Typography)`
  line-height: 18px !important;
`;

const Section = styled.section`
  padding: 14px 12px 16px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
`;

const Columns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const StatusColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
  gap: 10px;
`;

const ColumnBody = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const IconSlot = styled.div`
  flex: 0 0 auto;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;

  & img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const NextIconSlot = styled(IconSlot)`
  & img {
    filter: grayscale(0.2) brightness(1.08) contrast(0.92);
    opacity: 0.88;
  }
`;

const MetricsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const MetricRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 12px;
`;

const MetricLabel = styled(Typography)`
  color: ${tokens.OFFWHITE_10};
  flex-shrink: 0;
`;

export { RunXStatus };
